import { cn, Tooltip, TooltipContent, TooltipTrigger } from 'shared-ui';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
    connectorFraction,
    SEMANTIC_ZOOM_LEVELS,
    getSemanticZoomLabel,
    getSemanticZoomMeta,
} from '../utils/semanticZoom';
import type { SemanticZoomLevel } from '../utils/semanticZoom';
import type { MapMode } from '../utils/mapLayers';

interface SemanticZoomControlProps {
    /** Which product is hosting the map. Relabels the District Meter Area step to "Air Valves" in SAVI mode. */
    mode: MapMode;
    currentLevel: SemanticZoomLevel;
    /**
     * The live MapLibre zoom, used to fill the connector leaving the active circle as the map zooms
     * through the level's band (0 at the band start, ~50% at the mid-band intermediate half-step).
     */
    zoom: number;
    onChange: (level: SemanticZoomLevel) => void;
}

export function SemanticZoomControl({ mode, currentLevel, zoom, onChange }: SemanticZoomControlProps) {
    const activeMeta = getSemanticZoomMeta(currentLevel);
    // Fraction the map has progressed through the active level's band → width of the connector
    // leaving the active dot. Connectors before the active dot are full; those after are empty.
    const activeConnectorFill = connectorFraction(activeMeta, zoom);
    // On larger viewports the control sits at the top of the map; on mobile it moves to the
    // bottom and drops its text label to save vertical space.
    const isDesktop = useMediaQuery('(min-width: 768px)');

    return (
        <div
            className={cn(
                'pointer-events-none absolute left-1/2 z-10 -translate-x-1/2',
                isDesktop ? 'top-4' : 'bottom-4',
            )}
        >
            <div
                role="group"
                aria-label="Zoom To View"
                className="border-border bg-background pointer-events-auto flex flex-col items-center gap-2 rounded-lg border px-4 py-3 shadow-sm"
            >
                {isDesktop && (
                    <span className="text-foreground text-xs leading-none font-medium select-none">Zoom To View</span>
                )}
                <div className="flex items-center">
                    {SEMANTIC_ZOOM_LEVELS.map((meta, index) => {
                        const isActive = meta.level === currentLevel;
                        const isCompleted = !isActive && meta.displayOrder < activeMeta.displayOrder;
                        const isInactive = meta.displayOrder > activeMeta.displayOrder;
                        const hasNext = index < SEMANTIC_ZOOM_LEVELS.length - 1;
                        // Connectors left of the active dot are full; the one leaving the active dot
                        // fills with the band progress (showing the intermediate half-step); the rest empty.
                        const connectorFill = isCompleted ? 1 : isActive ? activeConnectorFill : 0;
                        const label = getSemanticZoomLabel(meta, mode);

                        return (
                            <div key={meta.level} className="flex items-center">
                                <Tooltip>
                                    <TooltipTrigger
                                        render={
                                            <button
                                                type="button"
                                                aria-label={label}
                                                aria-pressed={meta.level === currentLevel}
                                                data-state={
                                                    isActive ? 'active' : isCompleted ? 'completed' : 'inactive'
                                                }
                                                onClick={() => {
                                                    onChange(meta.level);
                                                }}
                                                className={cn(
                                                    // relative + z-10 keeps each dot (and its active ring) painted
                                                    // above the connector lines, which are later DOM siblings.
                                                    'relative z-10 size-2.5 cursor-pointer rounded-full transition-colors',
                                                    'focus-visible:ring-origin-blue/50 focus-visible:ring-3 focus-visible:ring-offset-1 focus-visible:outline-none',
                                                    // zoom-pulse plays a brief selection pulse (index.css). It lives only
                                                    // on the active dot, so selecting a different level moves the class to
                                                    // that (different) DOM element, which restarts the animation without a
                                                    // remount. The pulse renders on a ::after ring so it never clobbers the
                                                    // active dot's own ring-3 (also box-shadow based).
                                                    isActive && 'zoom-pulse bg-origin-blue ring-origin-blue/50 ring-3',
                                                    isCompleted && 'bg-origin-blue',
                                                    isInactive && 'bg-secondary dark:bg-accent',
                                                )}
                                            />
                                        }
                                    />
                                    <TooltipContent side="bottom">{label}</TooltipContent>
                                </Tooltip>
                                {hasNext && (
                                    <div
                                        aria-hidden
                                        // -mx-px tucks the connector 1px under each adjacent dot (which sit
                                        // above it via z-10) to close the sub-pixel seam at the round edge.
                                        className="bg-secondary dark:bg-accent -mx-px h-1 w-7 overflow-hidden"
                                    >
                                        {/* Inner fill grows left-to-right with band progress. */}
                                        <div
                                            className="bg-origin-blue h-full transition-[width]"
                                            style={{ width: `${(connectorFill * 100).toFixed(1)}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
